import { DoughnutChartData } from '../../data-visualization/doughnut-chart/doughnut-chart.component';

// ── Gateway enums ─────────────────────────────────────────────────────────────

export enum GatewayTransactionType {
  Initial = 'Initial',
  Rebill = 'Rebill',
  OneTime = 'One-Time',
}

export enum GatewayReportField {
  DayOfWeek = 'day_of_week',
  SalesType = 'sales_type',
  CardType = 'card_type',
  PaymentMethod = 'payment_method',
  ProductName = 'product_name',
  CampaignName = 'campaign_name',
  IssuerBankName = 'issuer_bank_name',
  CardBin = 'card_bin',
}

export enum GatewayKpiKey {
  GrossRevenue = 'gross_revenue',
  Transactions = 'transactions',
  Approvals = 'approvals',
  Declines = 'declines',
  Pending = 'pending',
  NetRevenue = 'net_revenue',
  RefundAmount = 'refund_amount',
  RefundCount = 'refund_count',
  AverageOrderValue = 'average_order_value',
  ApprovalRate = 'approval_rate',
  PendingSettlement = 'pending_settlement',
  PendingSettlementCount = 'pending_settlement_count',
}

export enum GatewayCountType {
  Gross = 'gross',
  Unique = 'unique',
}

/** Shared extra params for all breakdown panel calls — caps results to 5 */
export const TOP5_EXTRA = { page_size: 5 };

// ── Interfaces ────────────────────────────────────────────────────────────────
export interface OverviewCard {
  key: string;
  label: string;
  value: number;
  trend_pct?: number | null;
}

export interface OverviewCardsResponse {
  cards: OverviewCard[];
}

export interface DetailsRow {
  dimension: string | number;
  transactions: number;
  approvals: number;
  declines: number;
  approved: number;
  approval_rate: number;
  refunds_pending: number;
  refund_amount?: number;
  refund_count?: number;
  refund_rate?: number;
  net_revenue?: number;
  gross_revenue?: number;
  pending?: number;
  captured_count?: number;
  average_order_value?: number;
  trend_pct?: number | null;
  [key: string]: unknown;
}

export interface DetailsTableResponse {
  results: DetailsRow[];
  summary_row: DetailsRow;
}

export interface FilterTopBarEntry {
  id: number;
  name: string;
}

export interface FilterTopBar {
  brands: FilterTopBarEntry[];
  gateways: FilterTopBarEntry[];
  merchants: FilterTopBarEntry[];
  stages: string[];
  count_types: { key: string; label: string }[];
}

export interface FilterOptionValue {
  id: number | string;
  name: string;
}

export interface BankBinGroup {
  name: string;
  bins: string[];
}

export interface FilterOption {
  key: string;
  label: string;
  type: string;
  values?: (string | FilterOptionValue)[];
  bin_groups?: BankBinGroup[];
  status?: string;
  reason?: string;
}

export interface FilterOptionsResponse {
  top_bar: FilterTopBar;
  filters: FilterOption[];
}

export interface GatewayDashboardFilters {
  start_date?: string;
  end_date?: string;
  compare_start_date?: string;
  compare_end_date?: string;
  merchant_id?: string | number;
  campaign_id?: string | number;
  order_type?: GatewayTransactionType;
  count_type?: GatewayCountType;
  issuer_bank_name?: string[];
  card_bin?: string[];
}

export interface GatewayFilterPayload {
  start_date: string;
  end_date: string;
  compare_start_date?: string;
  compare_end_date?: string;
  merchant_id?: string | number;
  campaign_id?: string | number;
  order_type?: string;
  count_type?: string;
}


export interface GatewayBreakdownItem {
  label: string;
  value: string | null;
  hint?: string;
  count?: string;
  valueColor?: string;
}

export interface GatewaySummaryCard {
  title: string;
  hint?: string;
  value: string | null;
  trend_pct: number | null;
  icon?: string;
  iconBg?: string;
  breakdown?: GatewayBreakdownItem[];
}

export interface PaymentMethodLegendRow {
  name: string;
  color: string;
  transactions: string;
  revenue: string;
  approvalRate: string;
}

export interface CardBrandRow {
  name: string;
  icon?: string;
  transactions: string;
  grossRevenue: string;
  approvalRate: string;
  refundRate: string;
  refundAmount: string;
  netRevenue: string;
  isTotal?: boolean;
}

export interface GatewayTableRow {
  name: string;
  netRevenue: string;
  transactions: string;
  approvalRate: string;
}

// ── Section state interfaces ───────────────────────────────────────────────────

export interface GatewayKpiSection {
  row1: GatewaySummaryCard[];
  row2: GatewaySummaryCard[];
  loading: boolean;
  noData: boolean;
}

export interface GatewayTypeSection {
  cards: GatewaySummaryCard[];
  loading: boolean;
  noData: boolean;
}

export interface GatewayCardBrandSection {
  rows: CardBrandRow[];
  total: CardBrandRow | null;
  loading: boolean;
  noData: boolean;
  title: string;
  tooltip: string;
}

export interface GatewayPaymentMethodSection {
  chartData: DoughnutChartData;
  legend: PaymentMethodLegendRow[];
  loading: boolean;
  noData: boolean;
  title: string;
  tooltip: string;
}

export interface GatewayTableSection {
  rows: GatewayTableRow[];
  loading: boolean;
  noData: boolean;
  title: string;
  subtitle: string;
  tooltip: string;
  nameCol: string;
}

// ── KPI cards (Row 1: Gross Revenue | Transactions | Net Revenue | Refunds
//              Row 2: Average Order Value | Approval Rate) ─────────────────────

export const gatewayKpiCards: GatewaySummaryCard[] = [
  {
    title: 'Gross Revenue',
    value: '$0.00',
    trend_pct: null,
    breakdown: [
      {
        label: 'Pending Settlement',
        value: '$0.00',
        hint: 'Transactions awaiting settlement from the payment processor.',
        count: '--',
      },
    ],
  },
  {
    title: 'Transactions',
    value: '--',
    trend_pct: null,
    breakdown: [
      { label: 'Approved', value: '--' },
      { label: 'Declined', value: '--' },
      { label: 'Pending',  value: '--' },
    ],
  },
  {
    title: 'Net Revenue',
    hint: 'Gross revenue minus refunds and chargebacks.',
    value: '$0.00',
    trend_pct: null,
  },
  {
    title: 'Refunds',
    value: '$0.00',
    trend_pct: null,
    breakdown: [
      { label: 'Quantity', value: '--' },
    ],
  },
  {
    title: 'Average Order Value',
    hint: 'Total revenue divided by number of approved transactions.',
    value: '$0.00',
    trend_pct: null,
  },
  {
    title: 'Approval Rate',
    hint: 'Percentage of transactions that were approved out of all attempted.',
    value: '--',
    trend_pct: null,
  },
];

// ── Transaction-type cards (Initial Orders | Rebills | One-Time Orders) ───────

export const gatewayTypeCards: GatewaySummaryCard[] = [
  {
    title: 'Initial Orders',
    icon: 'initial-orders-icon.svg',
    iconBg: '#EEF2FF',
    value: '--',
    trend_pct: null,
    breakdown: [
      { label: 'Gross Revenue', value: '$0.00' },
      { label: 'Approved',      value: '--' },
      { label: 'Approval Rate', value: '--', valueColor: '#0CBE68' },
    ],
  },
  {
    title: 'Rebills',
    icon: 'rebills-icon.svg',
    iconBg: '#EFF6FF',
    value: '--',
    trend_pct: null,
    breakdown: [
      { label: 'Gross Revenue', value: '$0.00' },
      { label: 'Approved',      value: '--' },
      { label: 'Approval Rate', value: '--', valueColor: '#0CBE68' },
    ],
  },
  {
    title: 'One-Time Orders',
    icon: 'one-time-orders-icon.svg',
    iconBg: '#FFF7ED',
    value: '--',
    trend_pct: null,
    breakdown: [
      { label: 'Gross Revenue', value: '$0.00' },
      { label: 'Approved',      value: '--' },
      { label: 'Approval Rate', value: '--', valueColor: '#0CBE68' },
    ],
  },
];

// ── Revenue by Payment Method doughnut ────────────────────────────────────────

export const gatewayPaymentMethodData: DoughnutChartData = {
  data: [],
  loading: true,
  noData: false,
  total: '',
  labels: ['Master Card', 'Visa', 'Amex', 'Discover', 'Apple Pay'],
  backgroundColors: [{ backgroundColor: ['#68D58F', '#A9D6E5', '#F7D36F', '#F49B84', '#F7BDAB'] }],
};

export const gatewayPaymentMethodLegend: PaymentMethodLegendRow[] = [
  { name: 'Master Card', color: '#68D58F', transactions: '--', revenue: '$0.00', approvalRate: '--' },
  { name: 'Visa',        color: '#A9D6E5', transactions: '--', revenue: '$0.00', approvalRate: '--' },
  { name: 'Amex',        color: '#F7D36F', transactions: '--', revenue: '$0.00', approvalRate: '--' },
  { name: 'Discover',    color: '#F49B84', transactions: '--', revenue: '$0.00', approvalRate: '--' },
  { name: 'Apple Pay',   color: '#F7BDAB', transactions: '--', revenue: '$0.00', approvalRate: '--' },
];

// ── Section initial state ─────────────────────────────────────────────────────

export const GatewayKpiSectionData: GatewayKpiSection = {
  row1: gatewayKpiCards.slice(0, 4),
  row2: gatewayKpiCards.slice(4),
  loading: true,
  noData: false,
};

export const GatewayTypeSectionData: GatewayTypeSection = {
  cards: gatewayTypeCards,
  loading: true,
  noData: false,
};

export const GatewayCardBrandSectionData: GatewayCardBrandSection = {
  rows: [],
  total: null,
  loading: true,
  noData: false,
  title: 'Performance by Card Brand',
  tooltip: 'Breakdown of transaction performance grouped by card brand.',
};

export const GatewayPaymentMethodSectionData: GatewayPaymentMethodSection = {
  chartData: { ...gatewayPaymentMethodData },
  legend: [...gatewayPaymentMethodLegend],
  loading: true,
  noData: false,
  title: 'Revenue by Payment Method',
  tooltip: 'Net revenue breakdown by payment method.',
};

export const GatewayTopProductsSectionData: GatewayTableSection = {
  rows: [],
  loading: true,
  noData: false,
  title: 'Top Products',
  subtitle: '(by Net Revenue)',
  tooltip: 'Top performing products ranked by net revenue.',
  nameCol: 'Product Name',
};

export const GatewayTopCampaignsSectionData: GatewayTableSection = {
  rows: [],
  loading: true,
  noData: false,
  title: 'Top Campaigns',
  subtitle: '(by Net Revenue)',
  tooltip: 'Top performing campaigns ranked by net revenue.',
  nameCol: 'Campaign',
};

// ── Lookup constants ──────────────────────────────────────────────────────────

export const CardBrandIcons: { [key: string]: string } = {
  'Visa':             'dashboard-visa-icon.svg',
  'Mastercard':       'dashboard-master-card-icon.svg',
  'American Express': 'dashboard-amex-card-icon.svg',
  'Discover':         'dashboard-discover-icon.svg',
};

export const PaymentMethodColors: string[] = ['#68D58F', '#A9D6E5', '#F7D36F', '#F49B84', '#F7BDAB'];

export const TypeMeta: { [dim: string]: { title: string; icon: string; iconBg: string } } = {
  [GatewayTransactionType.Initial]: { title: 'Initial Orders',  icon: 'initial-orders-icon.svg',  iconBg: '#EEF2FF' },
  [GatewayTransactionType.Rebill]:  { title: 'Rebills',          icon: 'rebills-icon.svg',          iconBg: '#EFF6FF' },
  [GatewayTransactionType.OneTime]: { title: 'One-Time Orders', icon: 'one-time-orders-icon.svg', iconBg: '#FFF7ED' },
};

